import { type ComponentPropsWithRef, memo, type ReactNode, useMemo, useRef, useState } from 'react';
import cx from 'clsx';
import { DialogTrigger, Button } from 'react-aria-components';
import { useIntl } from 'react-intl';
import { buttonStyles } from '@repo/design-system-kit';
import { ActionSlot } from '#modular-table';
import styles from './styles.module.css';

type Alignment = 'left' | 'right';

export interface BaseCellProps extends ComponentPropsWithRef<'div'> {
  align?: Alignment;
  className?: string;
  onCellClick?: () => void;
  actionSlot?: ReactNode;
  actionSlotAlignment?: Alignment;
  showActionSlotOnRowHover?: boolean;
  popoverClassName?: string;
  popoverSlot?: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  withPopoverTriggerButton?: boolean;
}

const alignmentMap: Record<Alignment, string> = {
  left: 'alignLeft',
  right: 'alignRight',
} as const;

const actionAlignmentMap: Record<Alignment, string> = {
  left: 'actionAlignLeft',
  right: 'actionAlignRight',
} as const;

export const BaseCell = memo(function BaseCell({
  children,
  align = 'left',
  className = 'body-2',
  onCellClick,
  actionSlot,
  actionSlotAlignment = 'left',
  showActionSlotOnRowHover = false,
  popoverClassName,
  popoverSlot,
  withPopoverTriggerButton = false,
  onOpenChange,
  ...props
}: BaseCellProps): ReactNode {
  const cellRef = useRef<HTMLDivElement>(null);
  const hasPopoverSlot = Boolean(popoverSlot);
  const hasActionSlot = Boolean(actionSlot);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { formatMessage } = useIntl();

  // Reset the padding-right that React Aria adds to prevent layout shift
  // See the progress of the issue: https://github.com/adobe/react-spectrum/issues/1216
  const handleOpenChange = (isOpen: boolean): void => {
    setIsPopoverOpen(isOpen);

    // Find the closest table ancestor and then its parent (scroll container)
    const scrollContainer = cellRef.current?.closest('table')?.parentElement;

    // The objective is here to detect whenever scrollbars are shown
    if (isOpen && scrollContainer && scrollContainer.clientWidth !== scrollContainer.offsetWidth) {
      document.documentElement.style.marginRight = '-15px';
    } else {
      document.documentElement.style.marginRight = '';
    }

    onOpenChange?.(isOpen);
  };

  const actionSlotContent = useMemo(() => {
    if (hasActionSlot) {
      return (
        <ActionSlot
          className={cx(
            styles.cellActions,
            styles[actionAlignmentMap[actionSlotAlignment]],
            showActionSlotOnRowHover ? styles.cellActionsDisplay : styles.cellActionsDisplay
          )}
        >
          {actionSlot}
        </ActionSlot>
      );
    }

    return null;
  }, [actionSlot, actionSlotAlignment, hasActionSlot, showActionSlotOnRowHover]);

  const popoverSlotContent = withPopoverTriggerButton ? (
    <Button
      aria-label={
        isPopoverOpen
          ? formatMessage({ id: 'shared-kit.a11y.buttons.close-aria-label' })
          : formatMessage({ id: 'shared-kit.a11y.buttons.open-aria-label' })
      }
      className={cx(
        buttonStyles.btn,
        buttonStyles['btn--medium'],
        buttonStyles['btn--tertiary'],
        buttonStyles['btn--icon-only']
      )}
      data-interactive
      data-testid="popover-trigger"
      type="button"
    >
      {children}
      {popoverSlot}
    </Button>
  ) : (
    <Button
      aria-label={
        isPopoverOpen
          ? formatMessage({ id: 'shared-kit.a11y.buttons.close-aria-label' })
          : formatMessage({ id: 'shared-kit.a11y.buttons.open-aria-label' })
      }
      className={cx(styles.cellPopoverTrigger, popoverClassName)}
      data-interactive
      data-testid="popover-trigger"
    >
      {children}
      {popoverSlot}
    </Button>
  );

  const cellContent = (
    <div
      ref={cellRef}
      {...props}
      className={cx(styles.cell, styles[alignmentMap[align]], className)}
    >
      {hasPopoverSlot ? popoverSlotContent : children}
      {onCellClick ? (
        <Button className={styles.cellButton} onPress={onCellClick} type="button" />
      ) : null}
      {actionSlotContent}
    </div>
  );

  if (hasPopoverSlot) {
    return <DialogTrigger onOpenChange={handleOpenChange}>{cellContent}</DialogTrigger>;
  }

  return cellContent;
});
